import { useState, useEffect, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Trash, PencilSimple, Warning, Users } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import OptimizedImage from "@/components/OptimizedImage";
import ClientFormDialog from "@/components/ClientFormDialog";
import { PageHeader } from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchClients = useCallback(async (searchTerm = "") => {
    try {
      const params = searchTerm.trim() ? { search: searchTerm.trim() } : {};
      const response = await axios.get(`${API}/clients`, { params });
      setClients(response.data);
    } catch (error) {
      toast.error("Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  }, []);

  // Single effect for fetching clients - handles both initial load and search
  useEffect(() => {
    // Only debounce when user is typing (search has changed from initial state)
    if (search === "") {
      // Initial load - no debounce needed
      fetchClients();
    } else {
      // User is searching - debounce the API call
      const timeoutId = setTimeout(() => {
        fetchClients(search);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [search, fetchClients]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const openCreateDialog = () => {
    setEditingClient(null);
    setShowDialog(true);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setShowDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/clients/${deleteId}`);
      toast.success("Cliente eliminado");
      setDeleteId(null);
      fetchClients(search);
    } catch (error) {
      toast.error("Erro ao eliminar cliente");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <PageHeader
        icon={Users}
        title="Clientes"
        actions={
          <Button
            className="btn-primary"
            onClick={openCreateDialog}
            data-testid="btn-create-client"
          >
            <Plus size={18} />
            Novo Cliente
          </Button>
        }
      />

      {/* Search */}
      <div className="search-input-container max-w-md">
        <MagnifyingGlass size={18} />
        <Input
          placeholder="Pesquisar por nome, cidade..."
          value={search}
          onChange={handleSearch}
          className="gresart-input pl-10"
          data-testid="search-clients"
        />
      </div>

      {/* Clients Grid */}
      {clients.length === 0 ? (
        <div className="gresart-card">
          <div className="empty-state">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
              <Plus size={32} className="text-stone-400" />
            </div>
            <h3 className="text-lg font-medium text-stone-900 mb-2">
              {search ? "Nenhum cliente encontrado" : "Ainda não há clientes"}
            </h3>
            <p className="text-stone-500 mb-4">
              {search ? "Tente outro termo de pesquisa" : "Comece adicionando o seu primeiro cliente"}
            </p>
            {!search && (
              <Button className="btn-primary" onClick={openCreateDialog} data-testid="btn-add-first-client">
                <Plus size={18} />
                Adicionar Cliente
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4 stagger-animation">
          {clients.map((client) => (
            <div key={client.id} className="gresart-card-interactive group relative" data-testid={`client-card-${client.id}`}>
              {/* Clickable area - logo and name */}
              <Link to={`/clients/${client.id}`} className="block" data-testid={`btn-view-${client.id}`}>
                <div className="flex flex-col items-center text-center p-2">
                  {client.logo_url ? (
                    <OptimizedImage 
                      src={client.logo_url} 
                      alt={`Logo ${client.name}`}
                      className="w-16 h-16 sm:w-24 sm:h-24 object-contain bg-white mb-2 sm:mb-3"
                      width={150}
                    />
                  ) : (
                    <div className="w-16 h-16 sm:w-24 sm:h-24 bg-stone-100 rounded-lg flex items-center justify-center mb-2 sm:mb-3 hover:bg-stone-200 transition-colors">
                      <span className="text-xl sm:text-3xl font-bold text-stone-400">{client.name.charAt(0)}</span>
                    </div>
                  )}
                  <h3 className="font-semibold text-stone-900 hover:text-[#00A19A] transition-colors text-xs sm:text-base line-clamp-2">{client.name}</h3>
                </div>
              </Link>

              {/* Action buttons - absolute positioned, always visible on mobile */}
              <div className="absolute top-1 right-1 sm:top-2 sm:right-2 flex gap-0.5 sm:gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditDialog(client); }}
                  className="p-1 sm:p-1.5 bg-white/90 text-stone-400 hover:text-[#00A19A] hover:bg-teal-50 rounded-full shadow-sm transition-colors"
                  data-testid={`btn-edit-${client.id}`}
                >
                  <PencilSimple size={14} className="sm:w-4 sm:h-4" />
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteId(client.id); }}
                  className="p-1 sm:p-1.5 bg-white/90 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-full shadow-sm transition-colors"
                  data-testid={`btn-delete-${client.id}`}
                >
                  <Trash size={14} className="sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <ClientFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        client={editingClient}
        onSaved={() => fetchClients(search)}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Serão também eliminados todos os produtos atribuídos a este cliente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
